// SPV Pool v1.0 PR 6 — Cash Flow tab. Composes the accounting banner, the
// period selector, and the five cash-flow sections (Inflows, Waterfall,
// Reserve Fund, Investor Distributions). The period is controlled here;
// switching the period type resets the resolved date range.

import { useMemo, useState } from "react";
import { Card } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import {
  useGetAccountingStatus,
  useGetPoolCashFlow,
} from "@/hooks/capitalMarkets/useSpvPools";
import type { CashFlowPeriod, SpvPoolDto } from "@/types/spvPool";
import { AccountingBanner } from "./cash-flow/AccountingBanner";
import { InflowsCard } from "./cash-flow/InflowsCard";
import { InvestorDistributionsCard } from "./cash-flow/InvestorDistributionsCard";
import { PeriodSelector } from "./cash-flow/PeriodSelector";
import { resolvePeriodRange } from "./cash-flow/periodRange";
import { ReserveFundCard } from "./cash-flow/ReserveFundCard";
import { WaterfallApplicationTable } from "./cash-flow/WaterfallApplicationTable";

export function CashFlowTab({ pool }: { pool: SpvPoolDto }) {
  const [period, setPeriod] = useState<CashFlowPeriod>("Monthly");

  // The resolved range follows the period type; changing the period
  // recomputes it for the current month / quarter / year.
  const range = useMemo(() => resolvePeriodRange(period), [period]);

  const accountingQuery = useGetAccountingStatus(pool.id);
  const cashFlowQuery = useGetPoolCashFlow(
    pool.id,
    period,
    range.start,
    range.end,
  );

  const cashFlow = cashFlowQuery.data;

  return (
    <div className="flex flex-col gap-space-4">
      <AccountingBanner status={accountingQuery.data} />

      <div className="flex items-center justify-between">
        <PeriodSelector period={period} onChange={setPeriod} />
        {cashFlow && (
          <span className="text-caption text-gray-mid">
            {cashFlow.periodStart} — {cashFlow.periodEnd}
          </span>
        )}
      </div>

      {cashFlowQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading cash flow…
          </p>
        </Card>
      ) : cashFlowQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0">
            {extractErrorMessage(cashFlowQuery.error)}
          </p>
        </Card>
      ) : !cashFlow ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            No cash flow data for this period.
          </p>
        </Card>
      ) : (
        <>
          <InflowsCard inflows={cashFlow.inflows} />
          {/* Payment System v3.0 PR 3 — waterfall consolidation banner
              (design spec §6.4). Shown during the migration window. */}
          <Card>
            <p className="text-small text-gray-mid m-0">
              <span className="font-semibold">Waterfall model updated.</span>{" "}
              Pool Cash Flow now uses the unified WF1+WF2 model. Historical
              runs are preserved with their original waterfall code (WF1-WF6).
              See the migration map at{" "}
              <code className="text-caption">
                docs/specs/payment-system/waterfall-migration-map-2026-05.json
              </code>
              .
            </p>
          </Card>
          <WaterfallApplicationTable waterfall={cashFlow.waterfall} />
          <ReserveFundCard
            poolId={pool.id}
            reserveFund={cashFlow.reserveFund}
          />
          <InvestorDistributionsCard
            distributions={cashFlow.investorDistributions}
          />
        </>
      )}
    </div>
  );
}
